/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import Model, { Providers } from '@tripian/model';
import classes from './KiwiRateDetailModal.scss';

interface IKiwiRateDetailModal {
  rate: Providers.Kiwi.KiwiRate;
  currency: string;
  nightCount: number;
  onClose: () => void;
  t: (value: Model.TranslationKey) => string;
}

const fmt = (val: string | number | undefined) => {
  const n = parseFloat(String(val ?? 0)) || 0;
  return `$ ${new Intl.NumberFormat('en-US').format(Math.round(n))}`;
};

const formatNightDate = (dateStr: string) => {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const KiwiRateDetailModal: React.FC<IKiwiRateDetailModal> = ({ rate, currency, nightCount, onClose, t }) => {
  const [subtotalOpen, setSubtotalOpen] = useState(false);
  const [taxesOpen, setTaxesOpen] = useState(false);

  const price = rate.price;
  const subtotal = price?.exclusiveTotal;
  const total = price?.inclusiveTotal;
  const taxesTotal = (parseFloat(price?.totalTaxes || '0') + parseFloat(price?.totalFeesAndSurcharges || '0')).toString();
  const nightlyRates = price?.nightlyRates || [];
  const feeTaxes = price?.feeTaxes || [];

  const nightLabel = nightCount > 0 ? `${t('kiwi.common.subtotal')} (${nightCount} ${nightCount > 1 ? t('kiwi.common.nights') : t('kiwi.common.night')})` : t('kiwi.common.subtotal');

  const content = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={classes.overlay} onClick={(e) => { e.stopPropagation(); onClose(); }}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.panel} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={classes.header}>
          <h3 className={classes.title}>{t('kiwi.rateDetail.title')}</h3>
          <button type="button" className={classes.closeBtn} onClick={onClose} aria-label={t('kiwi.common.close')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <hr className={classes.divider} />

        <div className={classes.body}>
          {/* Column headers */}
          <div className={classes.colHeader}>
            <span className={classes.colHeaderLabel}>{t('kiwi.common.details')}</span>
            <span className={classes.colHeaderCurrency}>{currency}</span>
          </div>

          <hr className={classes.dividerThick} />

          {/* Subtotal row */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className={classes.expandRow}
            onClick={() => setSubtotalOpen((v) => !v)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSubtotalOpen((v) => !v); }}
          >
            <div className={classes.expandLeft}>
              <span className={classes.expandLabel}>{nightLabel}</span>
              <svg
                className={`${classes.expandChevron} ${subtotalOpen ? classes.open : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                xmlns="http://www.w3.org/2000/svg"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            <span className={classes.expandAmount}>{fmt(subtotal)}</span>
          </div>

          {subtotalOpen && nightlyRates.length > 0 && (
            <div className={classes.expandedRows}>
              {nightlyRates.map((nr, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className={classes.expandedRow}>
                  <span className={classes.expandedRowLabel}>{formatNightDate(nr.date)}</span>
                  <span className={classes.expandedRowAmount}>{fmt(nr.exclusiveRate)}</span>
                </div>
              ))}
            </div>
          )}

          <hr className={classes.divider} />

          {/* Hotel taxes & fees row */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className={classes.expandRow}
            onClick={() => setTaxesOpen((v) => !v)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTaxesOpen((v) => !v); }}
          >
            <div className={classes.expandLeft}>
              <span className={classes.expandLabel}>{t('kiwi.common.hotelTaxesFees')}</span>
              <svg
                className={`${classes.expandChevron} ${taxesOpen ? classes.open : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                xmlns="http://www.w3.org/2000/svg"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            <span className={classes.expandAmount}>{fmt(taxesTotal)}</span>
          </div>

          {taxesOpen && (
            <div className={classes.expandedRows}>
              {feeTaxes.length > 0 ? (
                feeTaxes.map((ft, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className={classes.expandedRow}>
                    <span className={classes.expandedRowLabel}>{ft.name || `Fee ${ft.feeTaxCodeId}`}</span>
                    <span className={classes.expandedRowAmount}>{fmt(ft.amount)}</span>
                  </div>
                ))
              ) : (
                <>
                  {parseFloat(price?.totalFeesAndSurcharges || '0') > 0 && (
                    <div className={classes.expandedRow}>
                      <span className={classes.expandedRowLabel}>{t('kiwi.common.fees')}</span>
                      <span className={classes.expandedRowAmount}>{fmt(price?.totalFeesAndSurcharges)}</span>
                    </div>
                  )}
                  {parseFloat(price?.totalTaxes || '0') > 0 && (
                    <div className={classes.expandedRow}>
                      <span className={classes.expandedRowLabel}>{t('kiwi.common.taxes')}</span>
                      <span className={classes.expandedRowAmount}>{fmt(price?.totalTaxes)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <hr className={classes.divider} />

          {/* Total */}
          <div className={classes.totalRow}>
            <span className={classes.totalLabel}>{t('kiwi.common.total')}</span>
            <span className={classes.totalAmount}>{fmt(total)}</span>
          </div>
        </div>

        <div className={classes.footer}>
          {t('kiwi.common.priceDisclaimer')}
        </div>

      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default KiwiRateDetailModal;
